// Shared helpers for StudentInfo popup
import { hasActiveReservationSubstitution } from "../../../utils/instructorReservations";

const clampInt = (n, min = 0) => {
   const x = Number(n);
   if (!Number.isFinite(x)) return min;
   return Math.max(min, Math.trunc(x));
};

// ISO -> "DD MM YYYY - HH:MM" (fără timezone shift)
function fmtIsoDDMMYYYY_HHMM(val) {
   if (val == null) return "—";
   if (typeof val === "string") {
      const m = val.match(/^(\d{4})-(\d{2})-(\d{2})[T\s](\d{2}):(\d{2})/);
      if (m) {
         const [, Y, M, D, h, min] = m;
         return `${D} ${M} ${Y} - ${h}:${min}`;
      }
      return String(val);
   }
   const d = val instanceof Date ? val : new Date(val);
   if (isNaN(d)) return "—";
   const pad = (n) => String(n).padStart(2, "0");
   return `${pad(d.getUTCDate())} ${pad(d.getUTCMonth() + 1)} ${d.getUTCFullYear()} - ${pad(
      d.getUTCHours(),
   )}:${pad(d.getUTCMinutes())}`;
}

function getReservationStartMs(r) {
   const v =
      r?.startTime ??
      r?.start ??
      r?.startAt ??
      r?.startISO ??
      r?.startDate ??
      null;
   if (v == null) return 0;
   if (typeof v === "string") {
      const p = Date.parse(v);
      return Number.isNaN(p) ? 0 : p;
   }
   const d = v instanceof Date ? v : new Date(v);
   const ms = d.getTime();
   return Number.isNaN(ms) ? 0 : ms;
}

function isReservationCancelled(r) {
   const flag =
      r?.isCancelled ??
      r?.is_cancelled ??
      r?.isCanceled ??
      r?.is_canceled ??
      null;
   if (flag !== null) return Boolean(flag);
   const st = String(r?.status || "").toLowerCase();
   return st === "cancelled" || st === "canceled";
}

function getReservationStudentId(r) {
   return (
      r?.studentId ??
      r?.student_id ??
      r?.student?.id ??
      r?.userId ??
      r?.user_id ??
      r?.user?.id ??
      null
   );
}

function resolveStudentId(src) {
   const candidates = [
      src?.id,
      src?.userId,
      src?.user_id,
      src?.studentId,
      src?.student_id,
      src?.user?.id,
      src?.student?.id,
   ];
   for (const v of candidates) {
      if (v === null || v === undefined) continue;
      const s = String(v).trim();
      if (!s || s === "null" || s === "undefined") continue;
      return s;
   }
   return "";
}

function readReservationDateParts(val) {
   const text = String(val || "").trim();
   if (!text) return null;

   const match = text.match(/(\d{4})-(\d{2})-(\d{2})(?:[T\s]+)(\d{2}):(\d{2})/);
   if (!match) return null;

   const [, year, month, day, hour, minute] = match;
   return { year, month, day, hour, minute };
}

function fmtReservationDate(val) {
   if (!val) return "—";

   const raw = readReservationDateParts(val);
   if (raw) return `${raw.day}.${raw.month}.${raw.year}`;

   const date = new Date(val);
   if (Number.isNaN(date.getTime())) return String(val);

   return date.toLocaleDateString("ro-RO", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      timeZone: "UTC",
   });
}

function fmtReservationTime(val) {
   if (!val) return "—";

   const raw = readReservationDateParts(val);
   if (raw) return `${raw.hour}:${raw.minute}`;

   const date = new Date(val);
   if (Number.isNaN(date.getTime())) return "—";

   return date.toLocaleTimeString("ro-RO", {
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "UTC",
   });
}

function readReservationField(item, ...keys) {
   for (const key of keys) {
      if (item?.[key] !== undefined && item?.[key] !== null) return item[key];
      if (
         item?.reservation?.[key] !== undefined &&
         item?.reservation?.[key] !== null
      ) {
         return item.reservation[key];
      }
   }

   return null;
}

function readPersonDisplayName(person) {
   if (!person || typeof person !== "object") return "";
   const fullName =
      `${person?.firstName || ""} ${person?.lastName || ""}`.trim();
   return fullName || String(person?.name || person?.fullName || "").trim();
}

function readReservationSubstituteInstructor(item) {
   if (!hasActiveReservationSubstitution(item)) return null;

   const substitution =
      item?.substitution ??
      item?.reservation?.substitution ??
      item?.instructorSubstitution ??
      item?.reservation?.instructorSubstitution ??
      null;

   return (
      item?.substituteInstructor ??
      item?.substitute_instructor ??
      substitution?.substituteInstructor ??
      substitution?.substitute_instructor ??
      item?.effectiveInstructor ??
      item?.effective_instructor ??
      item?.reservation?.effectiveInstructor ??
      item?.reservation?.effective_instructor ??
      null
   );
}

// Instructorul cu care se face efectiv lecția: înlocuitorul zilei, când
// există (backend-ul îl pune pe rezervare ca `substitution` +
// `effectiveInstructor`), altfel instructorul de bază al rezervării.
function readReservationDisplayInstructor(item) {
   const base = item?.instructor ?? item?.reservation?.instructor ?? null;
   const substitute = readReservationSubstituteInstructor(item);

   if (substitute) {
      const substituteName = readPersonDisplayName(substitute);
      const substituteId = String(substitute?.id ?? "").trim();
      const baseId = String(
         base?.id ?? item?.instructorId ?? item?.instructor_id ?? "",
      ).trim();
      if (substituteName) {
         return {
            instructor: substitute,
            name: substituteName,
            isSubstitute: !baseId || !substituteId || substituteId !== baseId,
         };
      }
   }

   return {
      instructor: base,
      name: readPersonDisplayName(base) || "—",
      isSubstitute: false,
   };
}

function readReservationInstructorName(item) {
   return readReservationDisplayInstructor(item).name;
}

function readReservationSector(item) {
   const value = String(
      readReservationField(item, "sector", "sectorName") || "",
   ).trim();
   return value || "—";
}

function readReservationGearbox(item) {
   const value = String(readReservationField(item, "gearbox") || "").trim();
   return value || "—";
}

function normalizeColorToCss(value, fallback = "var(--event-default)") {
   const raw = String(value || "").trim();

   if (!raw) return fallback;
   if (raw.startsWith("var(")) return raw;

   if (raw.startsWith("--")) {
      const short = raw.slice(2).trim().toLowerCase();
      if (!short || short === "default") return "var(--event-default)";
      if (short.startsWith("event-")) return `var(--${short})`;
      return `var(--event-${short})`;
   }

   if (/^event-/i.test(raw)) return `var(--${raw.toLowerCase()})`;
   return raw;
}

function readReservationColor(item) {
   return normalizeColorToCss(
      readReservationField(
         item,
         "color",
         "colorToken",
         "eventColor",
         "_colorToken",
      ) ??
         item?.colorToken ??
         item?._colorToken ??
         null,
      "var(--event-default)",
   );
}

function isReservationFavorite(item) {
   return Boolean(
      readReservationField(item, "isFavorite", "is_favorite") === true,
   );
}

function isReservationImportant(item) {
   return Boolean(
      readReservationField(item, "isImportant", "is_important") === true,
   );
}

function getReservationRowKey(item, index) {
   const id =
      item?.id ??
      item?._id ??
      item?.reservationId ??
      item?.reservation?.id ??
      item?.reservation?._id ??
      null;

   if (id != null) return `reservation-${String(id)}`;
   return `reservation-${String(getReservationStartMs(item))}-${index}`;
}

function getReservationMergeKey(item, fallback = "") {
   const id =
      readReservationField(item, "id", "_id", "reservationId", "reservation_id") ??
      null;
   if (id != null) return `id:${String(id).trim()}`;

   const startRaw =
      readReservationField(
         item,
         "startTime",
         "start",
         "startAt",
         "startISO",
         "startDate",
      ) ?? "";
   const startSig = String(startRaw || getReservationStartMs(item) || "").trim();
   const studentSig = String(getReservationStudentId(item) ?? "").trim();
   const instructorSig = String(
      readReservationField(item, "instructorId", "instructor_id") ??
         item?.instructor?.id ??
         item?.reservation?.instructor?.id ??
         "",
   ).trim();

   const signature = `${startSig}|${studentSig}|${instructorSig}`;
   if (signature.replace(/\|/g, "").trim()) return `sig:${signature}`;
   return fallback ? `fallback:${fallback}` : "";
}

function normalizeReservationId(value) {
   if (value == null) return "";
   return String(value).trim();
}

const RESERVATION_ID_KEYS = [
   "id",
   "_id",
   "reservationId",
   "reservation_id",
   "reservationID",
];

function getReservationPrimaryId(item) {
   return readReservationField(item, ...RESERVATION_ID_KEYS);
}

function getReservationLessonKey(item, fallback = "") {
   const id = normalizeReservationId(getReservationPrimaryId(item));
   if (id) return `id:${id}`;

   const slotSig = getReservationSlotSignature(item);
   if (slotSig) return `slot:${slotSig}`;

   const mergeKey = getReservationMergeKey(item, fallback);
   return mergeKey || (fallback ? `fallback:${fallback}` : "");
}

function normalizeReservationStartSignature(value) {
   if (!value) return "";

   const parts = readReservationDateParts(value);
   if (parts) {
      return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`;
   }

   const ms = new Date(value).getTime();
   return Number.isFinite(ms) ? String(ms) : String(value).trim();
}

function getReservationStartSignature(item) {
   const startRaw =
      readReservationField(
         item,
         "startTime",
         "start",
         "startAt",
         "startISO",
         "startDate",
      ) ?? "";

   return (
      normalizeReservationStartSignature(startRaw) ||
      String(getReservationStartMs(item) || "").trim()
   );
}

function isCurrentReservation(item, currentId, currentStart) {
   const wantedId = normalizeReservationId(currentId);
   if (wantedId) {
      const itemId = normalizeReservationId(getReservationPrimaryId(item));
      if (itemId && itemId === wantedId) return true;
   }

   const wantedStart = normalizeReservationStartSignature(currentStart);
   if (!wantedStart) return false;

   return getReservationStartSignature(item) === wantedStart;
}

function isTemporaryReservationId(value) {
   const raw = normalizeReservationId(value).toLowerCase();
   if (!raw) return false;
   return (
      raw.startsWith("tmp-") ||
      raw.startsWith("temp-") ||
      raw.startsWith("optimistic-") ||
      raw.startsWith("draft-")
   );
}

function isOptimisticReservation(item) {
   if (!item || typeof item !== "object") return false;
   if (item._optimistic === true || item._optimisticPending === true) return true;
   const id = readReservationField(item, "id", "_id", "reservationId", "reservation_id");
   return isTemporaryReservationId(id);
}

function getReservationUpdatedAtMs(item) {
   const raw =
      readReservationField(
         item,
         "updatedAt",
         "updated_at",
         "modifiedAt",
         "modified_at",
         "createdAt",
         "created_at",
      ) ??
      item?.reservation?.updatedAt ??
      item?.reservation?.updated_at ??
      item?.reservation?.modifiedAt ??
      item?.reservation?.modified_at ??
      item?.reservation?.createdAt ??
      item?.reservation?.created_at ??
      null;

   const ms = raw ? new Date(raw).getTime() : 0;
   return Number.isFinite(ms) ? ms : 0;
}

function getReservationSlotSignature(item) {
   const startRaw =
      readReservationField(
         item,
         "startTime",
         "start",
         "startAt",
         "startISO",
         "startDate",
      ) ?? "";
   const startParts = readReservationDateParts(startRaw);
   const startSig = startParts
      ? `${startParts.year}-${startParts.month}-${startParts.day}T${startParts.hour}:${startParts.minute}`
      : String(getReservationStartMs(item) || "").trim();

   const studentSig = String(getReservationStudentId(item) ?? "").trim();
   const instructorSig = String(
      readReservationField(item, "instructorId", "instructor_id") ??
         item?.instructor?.id ??
         item?.reservation?.instructor?.id ??
         "",
   ).trim();

   const signature = `${startSig}|${studentSig}|${instructorSig}`;
   return signature.replace(/\|/g, "").trim() ? signature : "";
}

function getReservationStabilityScore(item) {
   const id = normalizeReservationId(
      readReservationField(item, "id", "_id", "reservationId", "reservation_id"),
   );
   let score = 0;

   if (id) score += 20;
   if (id && !isTemporaryReservationId(id)) score += 80;
   if (!isOptimisticReservation(item)) score += 50;
   if (getReservationUpdatedAtMs(item) > 0) score += 10;

   return score;
}

function shouldPreferIncomingReservation(current, incoming) {
   if (!current) return true;
   if (!incoming) return false;

   const currentScore = getReservationStabilityScore(current);
   const incomingScore = getReservationStabilityScore(incoming);
   if (incomingScore !== currentScore) return incomingScore > currentScore;

   const currentUpdatedMs = getReservationUpdatedAtMs(current);
   const incomingUpdatedMs = getReservationUpdatedAtMs(incoming);
   if (incomingUpdatedMs !== currentUpdatedMs) {
      return incomingUpdatedMs > currentUpdatedMs;
   }

   return getReservationStartMs(incoming) >= getReservationStartMs(current);
}

// Înlocuirea și instructorul efectiv vin doar pe unele surse (lista elevului
// e îmbogățită de backend, lista globală poate avea `null`); la îmbinare
// păstrăm valoarea cunoscută, ca profilul să arate înlocuitorul zilei.
const SUBSTITUTION_EMBED_KEYS = [
   "substitution",
   "instructorSubstitution",
   "effectiveInstructor",
   "effective_instructor",
   "substituteInstructor",
   "substitute_instructor",
];

function mergeReservationRecords(base, patch) {
   if (!base) return patch || null;
   if (!patch) return base;

   const merged = {
      ...base,
      ...patch,
      reservation:
         base?.reservation || patch?.reservation
            ? {
                 ...(base?.reservation || {}),
                 ...(patch?.reservation || {}),
              }
            : undefined,
      instructor:
         base?.instructor || patch?.instructor
            ? {
                 ...(base?.instructor || {}),
                 ...(patch?.instructor || {}),
              }
            : undefined,
      student:
         base?.student || patch?.student
            ? {
                 ...(base?.student || {}),
                 ...(patch?.student || {}),
              }
            : undefined,
      user:
         base?.user || patch?.user
            ? {
                 ...(base?.user || {}),
                 ...(patch?.user || {}),
              }
            : undefined,
   };

   for (const key of SUBSTITUTION_EMBED_KEYS) {
      if (patch?.[key] == null && base?.[key] != null) {
         merged[key] = base[key];
      }
   }

   return merged;
}

function translateAttemptStatus(status) {
   const value = String(status || "")
      .trim()
      .toLowerCase();

   if (value === "passed") return "Promovat";
   if (value === "completed" || value === "finished") return "Finalizat";
   if (value === "failed" || value === "error") return "Picat";
   if (value === "expired") return "Expirat";
   if (
      value === "in_progress" ||
      value === "inprogress" ||
      value === "running"
   ) {
      return "În progres";
   }

   if (value === "not_started") return "Neînceput";
   return value ? value : "Necunoscut";
}

// ✅ extras helpers (copiate logic din PP)
function readBooleanField(src, keys, fallback = false) {
   if (!src || typeof src !== "object") return fallback;

   for (const key of keys) {
      const value = src?.[key];
      if (typeof value === "boolean") return value;
      if (value === 1 || value === "1") return true;
      if (value === 0 || value === "0") return false;
      if (typeof value === "string") {
         const normalized = value.trim().toLowerCase();
         if (normalized === "true") return true;
         if (normalized === "false") return false;
      }
   }

   return fallback;
}

function hasCanBookPracticalExamField(src) {
   if (!src || typeof src !== "object") return false;
   if (typeof src.__hasCanBookPracticalExam === "boolean") {
      return src.__hasCanBookPracticalExam;
   }

   return [
      "canBookPracticalExam",
      "can_book_practical_exam",
      "practicalExamAccess",
      "practical_exam_access",
   ].some((key) => Object.prototype.hasOwnProperty.call(src, key));
}

const normExtras = (src) => {
   const hasPracticalAccessField = hasCanBookPracticalExamField(src);

   return {
      medical_documents: Boolean(src?.medical_documents),
      individual_work: Boolean(src?.individual_work),
      // „Din altă școală” — pentru „De sunat” (după prima și după ultima lecție)
      fromOtherSchool: Boolean(src?.fromOtherSchool),
      number_of_absences: clampInt(src?.number_of_absences ?? 0, 0),
      canBookPracticalExam: hasPracticalAccessField
         ? readBooleanField(src, [
              "canBookPracticalExam",
              "can_book_practical_exam",
              "practicalExamAccess",
              "practical_exam_access",
           ])
         : false,
      __hasCanBookPracticalExam: hasPracticalAccessField,
   };
};

const buildFormDataFromStudent = (src) => ({
   firstName: src?.firstName || "",
   lastName: src?.lastName || "",
   email: src?.email || "",
   phone: src?.phone || "",
   idnp: src?.idnp != null ? String(src.idnp) : "",
   groupId: src?.groupId != null ? String(src.groupId) : "",
});

/* ===== Student avatar helpers (same logic ca în StudentItem / popup-urile de profil) ===== */

const firstLetter = (v) =>
   String(v || "")
      .trim()
      .charAt(0) || "";

function getInitials(student) {
   const fn = String(student?.firstName || "").trim();
   const ln = String(student?.lastName || "").trim();

   const a = firstLetter(fn);
   const b = firstLetter(ln);
   if (a && b) return (a + b).toUpperCase();

   const two = fn.slice(0, 2);
   if (two) return two.toUpperCase();

   return "–";
}

function hashStringToUInt(str) {
   let h = 0;
   for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) | 0;
   return h >>> 0;
}

const AVATAR_HUES = [
   { h: 70, s: 75 },
   { h: 0, s: 100 },
   { h: 30, s: 100 },
   { h: 54, s: 95 },
   { h: 130, s: 65 },
   { h: 210, s: 90 },
   { h: 255, s: 98 },
   { h: 285, s: 100 },
   { h: 330, s: 96 },
];

const AVATAR_LIGHTNESSES = [94, 92, 90, 88, 86, 84, 82, 80, 78, 76, 74];

const AVATAR_COLORS = AVATAR_HUES.flatMap(({ h, s }) =>
   AVATAR_LIGHTNESSES.map((l) => `hsl(${h} ${s}% ${l}%)`),
);

function getAvatarColorFromKey(key) {
   const k = String(key ?? "").trim();
   if (!k) return null;
   const idx = hashStringToUInt(k) % AVATAR_COLORS.length;
   return AVATAR_COLORS[idx];
}

function getAvatarColorFromName(student) {
   const fullName =
      `${student?.firstName || ""} ${student?.lastName || ""}`.trim();
   const hasLetter = /[A-Za-z\u00C0-\u024F\u0400-\u04FF]/.test(fullName);
   if (!hasLetter) return null;

   let normalized = fullName;
   try {
      normalized = fullName.normalize("NFKD");
   } catch {}
   const idx = hashStringToUInt(normalized) % AVATAR_COLORS.length;
   return AVATAR_COLORS[idx];
}

function isLikelyCssColor(v) {
   const s = String(v ?? "").trim();
   if (!s) return false;
   if (s.startsWith("var(")) return true;
   if (/^#([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(s)) return true;
   if (/^(rgb|rgba|hsl|hsla)\(/i.test(s)) return true;
   return false;
}

export {
   clampInt,
   firstLetter,
   fmtIsoDDMMYYYY_HHMM,
   getReservationStartMs,
   isReservationCancelled,
   getReservationStudentId,
   resolveStudentId,
   readReservationDateParts,
   fmtReservationDate,
   fmtReservationTime,
   readReservationField,
   readReservationDisplayInstructor,
   readReservationInstructorName,
   readReservationSector,
   readReservationGearbox,
   normalizeColorToCss,
   readReservationColor,
   isReservationFavorite,
   isReservationImportant,
   getReservationRowKey,
   getReservationMergeKey,
   normalizeReservationId,
   RESERVATION_ID_KEYS,
   getReservationPrimaryId,
   getReservationLessonKey,
   normalizeReservationStartSignature,
   getReservationStartSignature,
   isCurrentReservation,
   isTemporaryReservationId,
   isOptimisticReservation,
   getReservationUpdatedAtMs,
   getReservationSlotSignature,
   getReservationStabilityScore,
   shouldPreferIncomingReservation,
   mergeReservationRecords,
   translateAttemptStatus,
   readBooleanField,
   hasCanBookPracticalExamField,
   normExtras,
   buildFormDataFromStudent,
   getInitials,
   getAvatarColorFromName,
   getAvatarColorFromKey,
   hashStringToUInt,
   isLikelyCssColor,
};
